/**
 * Report Utilities
 * Report utilities: open in new window, download, generate URL
 */

import { getReportBaseUrl, getApiUrl } from './apiConfig';
import { authHeaders } from './role';

export type ReportLanguage = 'en' | 'ru';

/**
 * Starts a BACKGROUND report render and returns its report_id. The heavy report build (minutes on a
 * cold run) used to block a synchronous GET past the proxy timeout → 504. Now the client kicks off
 * generation here (returns instantly), then opens the report by report_id, which serves a fast
 * self-polling "generating" page that swaps itself for the report the moment it is ready.
 * Returns null when async generation is unavailable (caller falls back to the direct GET).
 */
export async function startReportGeneration(symbol: string, language: ReportLanguage = 'en'): Promise<string | null> {
  const sym = symbol.toLowerCase().trim();
  if (!sym) return null;
  try {
    const res = await fetch(getApiUrl(`/api/crypto/${encodeURIComponent(sym)}/report/generate?lang=${language}`), {
      method: 'POST',
      credentials: 'include',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      // A commercial-gate rejection (quota reached / asset not report-eligible / data unavailable) MUST
      // surface — never fall back to the direct GET, which would bypass the gate. Only the specific
      // "async store unavailable → use direct GET" case falls back (returns null).
      let msg = `Report generation failed (HTTP ${res.status}).`;
      try { const b = await res.json(); if (b?.error) msg = String(b.error); } catch { /* keep generic */ }
      if (res.status === 503 && /use direct get/i.test(msg)) return null;
      throw new Error(msg);
    }
    const j = await res.json();
    if (j?.success && j?.report_id) return String(j.report_id);
  } catch (e) {
    const msg = e instanceof Error ? e.message : '';
    // A network error (endpoint unreachable) → null so the caller falls back to the direct GET. A gate
    // error (quota / not eligible / data unavailable) is re-thrown so it reaches the user unmodified.
    if (/failed to fetch|networkerror|load failed/i.test(msg)) {
      return null;
    }
    throw e instanceof Error ? e : new Error('Report generation failed.');
  }
  return null;
}

export interface ReportOptions {
  symbol: string;
  language?: ReportLanguage;
  format?: 'html' | 'download';
  nocache?: boolean;
  /** When set, opens that stored report (Council verdict merge). Skips last-id fetch. */
  reportId?: string;
  /** When false, never calls /api/reports/last-id (default true unless nocache). */
  includeStoredReportId?: boolean;
  /** Report template type (institutional, premium, …). */
  type?: string;
  onOpen?: (window: Window | null) => void;
  onError?: (error: Error) => void;
}

/** Latest Redis-backed report id for symbol (auth cookies sent when same-origin / credentialed). */
export async function fetchLastReportIdForSymbol(symbol: string): Promise<string | null> {
  const sym = symbol.trim().toUpperCase();
  if (!sym) return null;
  try {
    const res = await fetch(getApiUrl(`/api/reports/last-id/${encodeURIComponent(sym)}`), {
      credentials: 'include',
    });
    const j = await res.json();
    if (j.success && j.report_id) return String(j.report_id);
  } catch {
    /* ignore */
  }
  return null;
}

export interface BuildReportUrlInput {
  symbol: string;
  language?: ReportLanguage;
  format?: 'html' | 'download';
  nocache?: boolean;
  type?: string;
  reportId?: string | null;
  includeStoredReportId?: boolean;
}

/** Builds `/api/crypto/{symbol}/report?...` with optional `report_id` for Council verdict HTML. */
export async function buildCryptoReportUrl(input: BuildReportUrlInput): Promise<string> {
  const base = getReportBaseUrl();
  const symbolLower = input.symbol.toLowerCase().trim();
  let reportId = input.reportId ?? undefined;
  const skipStored =
    input.nocache || input.includeStoredReportId === false || !!reportId;
  if (!skipStored) {
    reportId = (await fetchLastReportIdForSymbol(symbolLower)) || undefined;
  }
  const params = new URLSearchParams();
  params.set('lang', input.language ?? 'en');
  if (input.type) params.set('type', input.type);
  if (input.nocache) params.set('nocache', 'true');
  if (input.format === 'download') params.set('format', 'download');
  if (reportId) params.set('report_id', reportId);
  return `${base}/api/crypto/${symbolLower}/report?${params.toString()}`;
}

/**
 * Downloads a report robustly. It FETCHES the URL and only saves the body as a file when it is an
 * actual report (HTML). On an error response — e.g. HTTP 403 `report_blocked` when the oversight
 * verdict is BLOCKED — it throws a clear message instead of saving the error JSON as a `.html`
 * file (the bug where a downloaded "report" opened to `{"error":"report_blocked",...}`).
 */
export async function downloadReportFile(url: string, filename: string): Promise<void> {
  const res = await fetch(url, { credentials: 'include' });
  const contentType = res.headers.get('content-type') || '';
  const looksJson = contentType.includes('application/json');
  if (!res.ok || looksJson) {
    let message = res.ok
      ? 'This report is not available for download.'
      : `Download failed (HTTP ${res.status}).`;
    try {
      const body = await res.json();
      if (body?.error === 'report_blocked') {
        message = 'Download blocked — the oversight verdict for this report is BLOCKED. You can still open it on-screen with View.';
      } else if (body?.message) {
        message = body.message;
      }
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new Error(message);
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

/**
 * Opens the report in a new browser window
 * @param options - Options for opening the report
 * @returns Window object or null if failed to open
 */
export const openReportInNewWindow = async (options: ReportOptions): Promise<Window | null> => {
  const {
    symbol,
    language = 'en',
    format = 'html',
    nocache = false,
    reportId,
    includeStoredReportId = true,
    type,
    onOpen,
    onError,
  } = options;

  // Open the tab SYNCHRONOUSLY, inside the click gesture, so pop-up blockers don't kill it — we
  // navigate it to the report URL once that URL is known (after the async "start generation" call).
  const win = typeof window !== 'undefined' ? window.open('about:blank', '_blank') : null;

  try {
    if (!symbol || symbol.trim() === '') {
      throw new Error('Symbol is required');
    }

    let reportUrl: string;
    // Fresh on-screen generate (no specific stored report_id): render in the BACKGROUND and open the
    // fast-polling view, so the tab never blocks on a multi-minute synchronous render (the 504 fix).
    if (format !== 'download' && !reportId) {
      const genId = await startReportGeneration(symbol, language);
      if (genId) {
        const base = getReportBaseUrl();
        const p = new URLSearchParams({ report_id: genId, skip_verdict: '1', lang: language });
        reportUrl = `${base}/api/crypto/${symbol.toLowerCase().trim()}/report?${p.toString()}`;
      } else {
        // Async path unavailable (no report store / error) → fall back to the direct synchronous GET.
        reportUrl = await buildCryptoReportUrl({
          symbol, language, format: 'html', nocache, type,
          reportId: null, includeStoredReportId: nocache ? false : includeStoredReportId,
        });
      }
    } else {
      reportUrl = await buildCryptoReportUrl({
        symbol, language,
        format: format === 'download' ? 'download' : 'html',
        nocache, type,
        reportId: reportId ?? null,
        includeStoredReportId: nocache ? false : includeStoredReportId,
      });
    }

    console.log('📊 Opening report in new window:', symbol.toUpperCase(), language, format);

    if (win) {
      win.location.href = reportUrl;
      win.focus();
      if (onOpen) onOpen(win);
      return win;
    }
    // Synchronous open was blocked — try a direct open as a last resort.
    const fallback = window.open(reportUrl, '_blank', 'noopener,noreferrer');
    if (fallback) {
      if (onOpen) onOpen(fallback);
      return fallback;
    }
    throw new Error('Failed to open new window. Please check if pop-ups are blocked.');
  } catch (error) {
    if (win) { try { win.close(); } catch { /* ignore */ } }
    const err = error instanceof Error ? error : new Error('Unknown error');
    console.error('❌ Error opening report:', err);
    if (onError) {
      onError(err);
    } else {
      // The message is the commercial-gate reason (quota / not eligible) or an open failure — both are
      // self-explanatory, so show it directly rather than wrapping it.
      alert(err.message);
    }
    return null;
  }
};

/**
 * Downloads the report as an HTML file
 * @param options - Options for downloading the report
 */
export const downloadReport = async (options: ReportOptions): Promise<void> => {
  const { symbol, language = 'en', nocache = false, reportId, includeStoredReportId = true, type, onError } = options;

  try {
    if (!symbol || symbol.trim() === '') {
      throw new Error('Symbol is required');
    }

    const reportUrl = await buildCryptoReportUrl({
      symbol,
      language,
      format: 'download',
      nocache,
      type,
      reportId: reportId ?? null,
      includeStoredReportId: nocache ? false : includeStoredReportId,
    });

    console.log('📥 Downloading report:');
    console.log(`   Asset: ${symbol.toUpperCase()}`);
    console.log(`   Language: ${language}`);
    console.log(`   URL: ${reportUrl}`);

    window.open(reportUrl, '_blank', 'noopener,noreferrer');
    console.log('✅ Download initiated');
  } catch (error) {
    const err = error instanceof Error ? error : new Error('Unknown error');
    console.error('❌ Error downloading report:', err);
    if (onError) {
      onError(err);
    } else {
      alert(`Failed to download report: ${err.message}`);
    }
  }
};

/**
 * Generates URL for the report (no async last-id; use buildCryptoReportUrl when verdict merge matters).
 */
export const getReportUrl = (
  symbol: string,
  language: ReportLanguage = 'en',
  format: 'html' | 'download' = 'html',
  nocache: boolean = false
): string => {
  const base = getReportBaseUrl();
  const symbolLower = symbol.toLowerCase().trim();
  const params = new URLSearchParams();
  params.append('lang', language);
  if (nocache) {
    params.append('nocache', 'true');
  }
  if (format === 'download') {
    params.append('format', 'download');
  }
  return `${base}/api/crypto/${symbolLower}/report?${params.toString()}`;
};

/**
 * Checks report availability for the symbol
 * @param symbol - Asset symbol
 * @returns Promise<boolean> - true if report is available
 */
export const checkReportAvailability = async (symbol: string): Promise<boolean> => {
  try {
    const base = getReportBaseUrl();
    const symbolLower = symbol.toLowerCase().trim();
    const checkUrl = `${base}/api/crypto/${symbolLower}/report?lang=en&check=true`;
    const response = await fetch(checkUrl, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error('Error checking report availability:', error);
    return false;
  }
};

/**
 * Download report as PDF (uses backend PDF renderer with chromium/wkhtmltopdf fallback).
 */
export const downloadPDFReport = (symbol: string, type = 'institutional', lang = 'en') => {
  const base = getReportBaseUrl();
  const url = `${base}/api/export/pdf/${symbol.toLowerCase().trim()}?type=${type}&lang=${lang}`;
  window.open(url, '_blank');
};
